import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, FileText, Trash2, AlertCircle, Calendar, Loader, CheckCircle } from 'lucide-react';
import { apiService } from '../services/api';
import './MeetingRequestDrawer.css';

/**
 * MeetingRequestDrawer - A reusable drawer component for sending meeting requests
 * 
 * Props:
 * - isOpen: boolean - Controls drawer visibility
 * - onClose: function - Called when drawer should close
 * - contact: object - The contact to request a meeting with (from F4C database)
 *   Expected shape: { contact_id, first_name, last_name, job_title, company_name, image_url, ic_contact_id }
 * - eventId: number - The F4C event ID
 * - icEventId: number - The iConnections event ID
 * - onSuccess: function - Optional callback when meeting request is sent successfully
 */
const MeetingRequestDrawer = ({ 
    isOpen, 
    onClose, 
    contact, 
    eventId,
    icEventId,
    onSuccess 
}) => {
    const fileInputRef = useRef(null);
    
    // Form state
    const [message, setMessage] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    
    // Request details from iConnections
    const [requestDetails, setRequestDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState('');

    const MAX_MESSAGE_LENGTH = 1000;
    const MAX_ATTACHMENTS = 3;
    const MAX_FILE_SIZE_MB = 5;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    // Fetch request details when drawer opens
    useEffect(() => {
        if (isOpen && contact?.ic_contact_id && icEventId) {
            fetchRequestDetails();
        }
        // Reset form when drawer closes
        if (!isOpen) {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, contact?.ic_contact_id, icEventId]);

    const fetchRequestDetails = async () => {
        setDetailsLoading(true);
        setDetailsError('');
        
        try {
            const response = await apiService.getMeetingRequestDetails(
                contact.ic_contact_id,
                icEventId
            );
            
            if (response.data.success) {
                setRequestDetails(response.data.data);
            } else {
                setDetailsError(response.data.error || 'Unable to load meeting request details');
            }
        } catch (err) {
            console.error('Error fetching request details:', err);
            const errorMessage = err.response?.data?.detail || 'Failed to load meeting request details';
            setDetailsError(errorMessage);
        } finally {
            setDetailsLoading(false);
        }
    };

    const resetForm = () => {
        setMessage('');
        setAttachments([]);
        setError('');
        setRequestDetails(null);
        setDetailsError('');
        setShowSuccess(false);
    };

    // Convert file to base64
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Remove the data:application/pdf;base64, prefix
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleMessageChange = (e) => {
        const value = e.target.value;
        if (value.length <= MAX_MESSAGE_LENGTH) {
            setMessage(value);
            setError('');
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setError('');

        // Check if adding these files would exceed the limit
        if (attachments.length + files.length > MAX_ATTACHMENTS) {
            setError(`You can only attach up to ${MAX_ATTACHMENTS} documents`);
            return;
        }

        const validFiles = [];
        const errors = [];

        files.forEach(file => {
            // Check file type
            if (file.type !== 'application/pdf') {
                errors.push(`"${file.name}" is not a PDF file`);
                return;
            }

            // Check file size
            if (file.size > MAX_FILE_SIZE_BYTES) {
                errors.push(`"${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB`);
                return;
            }

            // Check for duplicates
            if (attachments.some(att => att.name === file.name)) {
                errors.push(`"${file.name}" is already attached`);
                return;
            }

            validFiles.push({
                file,
                name: file.name,
                size: file.size,
                id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            });
        });

        if (errors.length > 0) {
            setError(errors.join('. '));
        }

        if (validFiles.length > 0) {
            setAttachments(prev => [...prev, ...validFiles]);
        }

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveAttachment = (attachmentId) => {
        setAttachments(prev => prev.filter(att => att.id !== attachmentId));
        setError('');
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const handleSubmit = async () => {
        if (!message.trim()) {
            setError('Please enter a message');
            return;
        }

        if (!contact?.ic_contact_id || !icEventId) {
            setError('Missing required information. Please try again.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            // Convert attachments to base64
            const attachmentData = await Promise.all(
                attachments.map(async (att) => {
                    const base64 = await fileToBase64(att.file);
                    return {
                        fileName: att.name,
                        fileType: 'application/pdf',
                        base64: base64,
                        fileSize: att.size
                    };
                })
            );

            // Send the meeting request
            const response = await apiService.sendMeetingRequest({
                to_ic_contact_id: contact.ic_contact_id,
                ic_event_id: icEventId,
                message: message.trim(),
                attachments: attachmentData
            });

            if (response.data.success) {
                setShowSuccess(true);
                // Call optional success callback
                if (onSuccess) {
                    onSuccess(response.data.meeting_id);
                }
                // Close drawer after showing success message
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                setError(response.data.error || 'Failed to send meeting request. Please try again.');
            }
        } catch (err) {
            console.error('Error submitting meeting request:', err);
            const errorMessage = err.response?.data?.detail || err.message || 'Failed to send meeting request. Please try again.';
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="meeting-drawer-overlay" onClick={onClose} />
            <div className={`meeting-request-drawer ${isOpen ? 'open' : ''}`}>
                {/* Header */}
                <div className="meeting-drawer-header">
                    <div className="meeting-drawer-header-content">
                        <Calendar size={20} className="meeting-drawer-header-icon" />
                        <h2>Request Meeting</h2>
                    </div>
                    <button className="meeting-drawer-close" onClick={onClose} title="Close">
                        <X size={20} />
                    </button>
                </div>

                <div className="meeting-drawer-content">
                    {/* Success Message */}
                    {showSuccess ? (
                        <div className="meeting-success-container">
                            <div className="meeting-success-icon">
                                <CheckCircle size={64} />
                            </div>
                            <h3 className="meeting-success-title">Meeting Request Sent!</h3>
                            <p className="meeting-success-message">
                                Your meeting request has been sent to {contact?.first_name} {contact?.last_name}.
                            </p>
                        </div>
                    ) : (
                        <>
                    {/* Meeting Credits - only show when loading, error, or has data */}
                    {(detailsLoading || detailsError || requestDetails) && (
                        <div className="meeting-credits-section">
                            {detailsLoading ? (
                                <div className="credits-loading">
                                    <Loader size={16} className="spinner-icon" />
                                    <span>Loading credits...</span>
                                </div>
                            ) : detailsError ? (
                                <div className="credits-error">
                                    <AlertCircle size={16} />
                                    <span>{detailsError}</span>
                                </div>
                            ) : requestDetails ? (
                                <div className="credits-display">
                                    <span className="credits-label">Meeting Credits Remaining:</span>
                                    <span className={`credits-value ${requestDetails.requestCredits <= 5 ? 'low' : ''}`}>
                                        {requestDetails.requestCredits}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Contact Card */}
                    {contact && (
                        <div className="meeting-recipient-section">
                            <h3 className="meeting-section-title">Sending Request To</h3>
                            <div className="meeting-recipient-card">
                                <div className="meeting-recipient-avatar">
                                    {contact.image_url ? (
                                        <img src={contact.image_url} alt={`${contact.first_name} ${contact.last_name}`} />
                                    ) : (
                                        <span>{getInitials(contact.first_name, contact.last_name)}</span>
                                    )}
                                </div>
                                <div className="meeting-recipient-info">
                                    <h4 className="meeting-recipient-name">
                                        {contact.first_name} {contact.last_name}
                                    </h4>
                                    {contact.job_title && (
                                        <p className="meeting-recipient-title">{contact.job_title}</p>
                                    )}
                                    {contact.company_name && (
                                        <p className="meeting-recipient-company">{contact.company_name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Message Section */}
                    <div className="meeting-message-section">
                        <div className="meeting-section-header">
                            <h3 className="meeting-section-title">Your Message</h3>
                            <span className={`character-count ${message.length > MAX_MESSAGE_LENGTH * 0.9 ? 'warning' : ''}`}>
                                {message.length} / {MAX_MESSAGE_LENGTH}
                            </span>
                        </div>
                        <textarea
                            className="meeting-message-input"
                            placeholder="Introduce yourself and explain why you'd like to meet..."
                            value={message}
                            onChange={handleMessageChange}
                            maxLength={MAX_MESSAGE_LENGTH}
                        />
                    </div>

                    {/* Attachments Section */}
                    <div className="meeting-attachments-section">
                        <div className="meeting-section-header">
                            <h3 className="meeting-section-title">
                                Attachments <span className="attachments-max">({MAX_ATTACHMENTS} Max)</span>
                            </h3>
                        </div>
                        
                        <div className="attachments-info">
                            <span>PDF files only, max {MAX_FILE_SIZE_MB}MB each</span>
                        </div>

                        {/* Attachment List */}
                        {attachments.length > 0 && (
                            <div className="attachments-list">
                                {attachments.map(attachment => (
                                    <div key={attachment.id} className="attachment-item">
                                        <FileText size={18} className="attachment-icon" />
                                        <div className="attachment-details">
                                            <span className="attachment-name">{attachment.name}</span>
                                            <span className="attachment-size">{formatFileSize(attachment.size)}</span>
                                        </div>
                                        <button
                                            className="attachment-remove-btn"
                                            onClick={() => handleRemoveAttachment(attachment.id)}
                                            title="Remove attachment"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Upload Button */}
                        {attachments.length < MAX_ATTACHMENTS && (
                            <button
                                className="upload-attachment-btn"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Upload size={18} />
                                <span>Upload PDF</span>
                            </button>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,application/pdf"
                            multiple
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="meeting-error-message">
                            <AlertCircle size={18} />
                            <span>{error}</span>
                        </div>
                    )}
                        </>
                    )}
                </div>

                {/* Footer - hide when showing success */}
                {!showSuccess && (
                    <div className="meeting-drawer-footer">
                        <button
                            className="meeting-cancel-btn"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            className="meeting-submit-btn"
                            onClick={handleSubmit}
                            disabled={isSubmitting || !message.trim() || detailsLoading}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader size={18} className="spinner-icon" />
                                    <span>Sending...</span>
                                </>
                            ) : (
                                <span>Send Request</span>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default MeetingRequestDrawer;

